import React, { useEffect, useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Select,
    MenuItem,
    InputLabel,
    FormControl,
    Typography,
    Box,
    Stack,
    Divider
} from "@mui/material";
import Grid from '@mui/material/Grid2';
import { useDispatch, useSelector } from "react-redux";
import { updateCandidatePipelineStatus } from "../redux/actions/candidateActions";
import { getCandidateTimelineForJobPostService } from "../redux/service/candidateService";
import { formatDateWithTime, formatDDMMYYYYWithSlash } from "../helpers/formattedDateTime";
import constants from '../helpers/constants.json';
import showToast from "../utils/toast";
import { Edit } from "@mui/icons-material";
import JoiningDetailsModal from "./JoiningDetailsModal";
import LightTooltip from "../components/LightTooltip";

const CandidateTimelineModal = ({ open, onClose, jobPostId, jobTitle, candidate }) => {
    const dispatch = useDispatch();
    const [history, setHistory] = useState([]);

    const [newStatus, setNewStatus] = useState("");
    const [newNote, setNewNote] = useState("");

    const [joiningDate, setJoiningDate] = useState(null);
    const [joiningDetailsModalVisible, setJoiningDetailsModalVisible] = useState(false);
    const [shouldCloseTimelineModal, setShouldCloseTimelineModal] = useState(false);

    const userDetails = useSelector((state) => state.user.userDetails);

    const mainStyle = {
        divStyle: {
            display: 'flex',
            justifyContent: 'flex-start',
            padding: '4px'
        },
        spanStyle: {
            color: 'gray',
            textAlign: 'right',
            marginRight: '8px',
            minWidth: '130px',
            width: '130px',
            fontSize: '14px'
        }
    };

    useEffect(() => {
        getCandidateTimeline();
    }, []);

    const getCandidateTimeline = async () => {
        let candidateTimeline = await getCandidateTimelineForJobPostService(candidate.jobPostCandidateId);
        setHistory(candidateTimeline.data);
    }

    const handleAddUpdate = async () => {

        if (!newStatus) return showToast("Please select a status.", 'error');
        if (newStatus === "JOINED" && !joiningDate) {
            showToast("Please select joining date.", 'error');
            return;
        }
        if (newStatus === "CANDIDATE_LEFT" && !joiningDate) {
            showToast("Please select candidate left date.", 'error');
            return;
        }
        const payload = {
            'status': newStatus,
            'note': newNote || "",
            'addedBy': userDetails?.adminUserId,
            'joiningDate': (newStatus === "JOINED" || newStatus === "CANDIDATE_LEFT") ? joiningDate : null
        }

        await dispatch(updateCandidatePipelineStatus(candidate.jobPostCandidateId, payload, jobPostId));
        if (newStatus === "JOINED") {
            setShouldCloseTimelineModal(true);
            setJoiningDetailsModalVisible(true);
        } else {
            onClose();
        }
    };

    const handleCloseTimelineModal = () => {
        onClose();
    }

    const checkIfJoiningDetailsPending = () => {
        return !candidate.monthlyCTC || !candidate.paymentAmount || !candidate.aadharCardDocId || !candidate.offerLetterDocId;
    }

    const selectionRules = {
        "CANDIDATE_LEFT": {
            disabled: !history.some(item => item.status == "JOINED"),
            reason: "You can't select 'Candidate Left' if candidate has not 'Joined'",
        },
        "LEFT_CLOSED": {
            disabled: !history.some(item => item.status == "CANDIDATE_LEFT"),
            reason: "You can't select 'Left Closed' if candidate has not 'Left'",
        },
        "SUPPORT_PERIOD_COMPLETED": {
            disabled: !history.some(item => item.status == "JOINED"),
            reason: "You can't select 'Support Period Completed' if candidate has not 'Joined'",
        },
        "TERMINATED": {
            disabled: !history.some(item => item.status == "JOINED"),
            reason: "You can't select 'Terminated' if candidate has not 'Joined'",
        },
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Candidate Pipeline - {candidate.candidateName} for {jobTitle}</DialogTitle>

            <DialogContent dividers>

                {/* --- Add New Update Section --- */}
                <Box sx={{ display: 'flex', flexDirection: 'row', columnGap: '12px', marginTop: '12px', alignItems: 'center' }}>
                    <FormControl fullWidth required>
                        <InputLabel>Status</InputLabel>
                        <Select
                            label="Status"
                            value={newStatus}
                            onChange={(e) => setNewStatus(e.target.value)}
                        >
                            {constants.allPipelineStatus.map((status) => {
                                const rule = selectionRules[status.id];
                                const isDisabled = rule?.disabled;
                                const reason = rule?.reason;
                                if (isDisabled) {
                                    return (
                                        <LightTooltip
                                            key={status.id}
                                            title={isDisabled ? reason : ""}
                                            placement="top-end"
                                            arrow
                                            disableInteractive
                                        >
                                            <span>
                                                <MenuItem key={status.id} value={status.id} disabled={isDisabled}>
                                                    {status.name}
                                                </MenuItem>
                                            </span>
                                        </LightTooltip>
                                    )
                                }

                                return (
                                    <MenuItem key={status.id} value={status.id}>
                                        {status.name}
                                    </MenuItem>
                                )
                            }
                            )}
                        </Select>
                    </FormControl>

                    <TextField
                        label="Add Note"
                        multiline
                        // rows={3}
                        fullWidth
                        value={newNote}
                        onChange={(e) => setNewNote(e.target.value)}
                    />
                    {/* --- JOINED Conditional Fields --- */}
                    {(newStatus === "JOINED" || newStatus === "CANDIDATE_LEFT") && (
                        <>
                            {/* Joining Date Selector */}
                            <TextField
                                label={newStatus == "JOINED" ? "Joining Date" : "Candidate Left Date"}
                                type="date"
                                slotProps={{
                                    inputLabel: {
                                        shrink: true
                                    }
                                }}
                                required
                                value={joiningDate || ""}
                                onChange={(e) => setJoiningDate(e.target.value)}
                                fullWidth
                            />

                        </>
                    )}

                </Box>

                {/* --- Timeline Section --- */}
                <Stack spacing={3} sx={{ position: "relative", pl: 3, mt: 1 }}>
                    {/* Vertical line */}
                    <Box
                        sx={{
                            position: "absolute",
                            left: "12px",
                            top: 0,
                            bottom: 0,
                            width: "2px",
                            backgroundColor: "primary.main",
                            opacity: 0.3,
                        }}
                    />
                    {history.map((item, idx) => (
                        <Box key={idx} sx={{ position: "relative" }}>
                            {/* Dot */}
                            <Box
                                sx={{
                                    position: "absolute",
                                    left: "-4px",
                                    top: "4px",
                                    width: "10px",
                                    height: "10px",
                                    borderRadius: "50%",
                                    backgroundColor: "primary.main",
                                }}
                            />
                            <Box sx={{ ml: 2 }}>
                                <Grid container>
                                    <Grid size={{ xs: 5.7 }}>
                                        <div>
                                            <Typography variant="subtitle1" fontWeight={600}>
                                                {item.status}
                                            </Typography>
                                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                                                {item.note}
                                            </Typography>
                                            <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                                                <Typography variant="caption" color="text.secondary">
                                                    <span style={{ fontWeight: 'bold' }}>Date:</span> {formatDateWithTime(item.createDate)}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    <span style={{ fontWeight: 'bold' }}>Added by:</span> {item.addedBy}
                                                </Typography>
                                            </Stack>
                                            {item.status == "JOINED" &&
                                                <Typography variant="caption" color="text.secondary">
                                                    <span style={{ fontWeight: 'bold' }}>Joining Date:</span> {formatDDMMYYYYWithSlash(item.joiningDate)}
                                                </Typography>
                                            }
                                            {item.status == "CANDIDATE_LEFT" &&
                                                <Typography variant="caption" color="text.secondary">
                                                    <span style={{ fontWeight: 'bold' }}>Candidate Left Date:</span> {formatDDMMYYYYWithSlash(item.joiningDate)}
                                                </Typography>
                                            }
                                        </div>
                                    </Grid>
                                    <Grid size={{ xs: 6.3 }} sx={{ borderLeft: item.status == "JOINED" && '1px solid #ddd', paddingLeft: '8px' }}>
                                        {item.status == "JOINED" &&
                                            <>
                                                <div>
                                                    <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                                                        <div><strong>Joining Details</strong><span> ({checkIfJoiningDetailsPending() ? "Pending" : "Completed"})</span></div>
                                                        {checkIfJoiningDetailsPending() &&
                                                            <Button variant="outlined" startIcon={<Edit />}
                                                                onClick={() => setJoiningDetailsModalVisible(true)}>
                                                                Update Now
                                                            </Button>
                                                        }
                                                    </div>
                                                    <div style={mainStyle.divStyle}>
                                                        <span style={mainStyle.spanStyle}>Replacement For:</span>
                                                        <span>{candidate.replacementFor ? candidate.replacementFor : "n/a"}</span>
                                                    </div>
                                                    <div style={mainStyle.divStyle}>
                                                        <span style={mainStyle.spanStyle}>Monthly CTC:</span>
                                                        <span>{candidate.monthlyCTC ? candidate.monthlyCTC : "---"}</span>
                                                    </div>
                                                    <div style={mainStyle.divStyle}>
                                                        <span style={mainStyle.spanStyle}>Payment Received:</span>
                                                        <span>{candidate.paymentAmount ? "Yes" : "No"} {candidate.paymentAmount ? `(${candidate.paymentAmount}/- paid via ${candidate.paymentMadeVia})` : ""}</span>
                                                    </div>
                                                    <div style={mainStyle.divStyle}>
                                                        <span style={mainStyle.spanStyle}>Offer Letter:</span>
                                                        <span>{candidate.offerLetterDocId ? "Uploaded" : "Pending"}</span>
                                                    </div>
                                                    <div style={mainStyle.divStyle}>
                                                        <span style={mainStyle.spanStyle}>Aadhar Card:</span>
                                                        <span>{candidate.aadharCardDocId ? "Uploaded" : "Pending"}</span>
                                                    </div>
                                                </div>
                                            </>
                                        }
                                    </Grid>
                                </Grid>
                            </Box>
                            {idx < history.length - 1 && <Divider sx={{ my: 1 }} />}
                        </Box>
                    ))}
                </Stack>

            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>Close</Button>
                <Button variant="contained" onClick={handleAddUpdate}>
                    Save Update
                </Button>
            </DialogActions>

            {
                joiningDetailsModalVisible &&
                <JoiningDetailsModal
                    open={true}
                    onClose={() => {
                        setJoiningDetailsModalVisible(false);
                        if (shouldCloseTimelineModal) {
                            handleCloseTimelineModal();
                        }
                    }}
                    closeTimelineModalCallback={shouldCloseTimelineModal ? handleCloseTimelineModal : null}
                    jobPostId={jobPostId}
                    jobTitle={jobTitle}
                    candidate={candidate}
                />
            }
        </Dialog >
    );
};

export default CandidateTimelineModal;
